<template>
    <div class="container container--bordereauLivraison-list">

        <Loader v-if="!isReady"></Loader>

        <List v-else
            :filtering="this"
            :itemNameSingular="'bordereau de livraison'"
            :itemNamePlural="'bordereaux de livraison'" >

            <template v-slot:actions>
                    
                <Button 
                    class=".native"
                    :disabled="!hasContent"
                    :loading="processingXlsx"
                    @click.native="downloadXlsx">
                    <span class="fa fa-file-excel-o"></span>
                    Télécharger
                </Button>
                    
            </template>

            <template v-slot:filters>

                <ListFilter class="filter--main">
                    <input v-model="filters.query"
                        type="text"
                        placeholder="Rechercher un n°, engagement, budget, etc."/>
                </ListFilter>
                <ClientFilter v-model="filters.client_id"
                    :clients="clients"/>
                <SiteFilter v-model="filters.site_id"
                    :clientAddresses="clientAddresses"
                    :markets="markets" />
                <ListFilter class="filter">
                    <Select v-model="filters.status"
                        :options="status"
                        all="Tous les status">
                    </Select>
                </ListFilter>
                <DateFilter v-model="dates" />

            </template>

            <template v-slot:heading>
                <div class="cell cell--main criteria">
                    <Sorter criteria="num">Numéro</Sorter>
                </div>
                <div class="cell criteria">
                    <Sorter criteria="clientCode">Client</Sorter>
                </div>
                <div class="cell">
                    Commande
                </div>
                <div class="cell criteria">
                    <Sorter criteria="expeditedAt">Expédiée le</Sorter>
                </div>
                <div class="cell criteria">
                    <Sorter criteria="deliveredAt">Livrée le</Sorter>
                </div>
                <div class="cell">
                    Adresse
                </div>
                <div class="cell criteria">
                    <Sorter criteria="deliveryMode">Mode de livraison</Sorter>
                </div>
                <div class="cell">
                    Statuts
                </div>
            </template>

            <ul class="list__content bordereau-livraison-list" v-if="hasContent">

                <li class="list__content__item"  
                    v-for="bordereauLivraison in items"
                    :key="bordereauLivraison.id">

                    <ListItem class="list-item--card"
                        :item="bordereauLivraison"
                        :route="route">
                        
                        <div class="cell cell--main">
                            <strong>{{ bordereauLivraison.num }}</strong>
                        </div>
                        <div class="cell cell--secondary">{{ bordereauLivraison.clientCode }}</div>
                        <div class="cell cell--secondary">{{ ( bordereauLivraison.commande ) ? bordereauLivraison.commande.num : 'Aucune' }}</div>
                        <div class="cell cell--date cell--secondary"><Date :date="bordereauLivraison.expeditedAt" /></div>
                        <div class="cell cell--date"><Date :date="bordereauLivraison.deliveredAt" time="true" relevantTime="true"/></div>
                        <div class="cell cell--secondary">
                            <abbr><Address :data="bordereauLivraison.deliveryAddress" /></abbr>
                        </div>
                        <div class="cell cell--secondary">{{ bordereauLivraison.deliveryModeLibelle }}</div>
                        <div class="cell cell--state">
                            <Badge type="info">{{ bordereauLivraison.status.libelle }}</Badge>
                            <Badge type="success" v-if="bordereauLivraison.status.isFacture">Facturée</Badge>
                            <Badge type="danger" v-if="bordereauLivraison.status.isRetourne">{{ bordereauLivraison.status.retourStatutLibelle }}</Badge>
                            
                            <span class="tracking">
                                <Badge type="info" v-if="bordereauLivraison.status.trackingURL">   
                                    <i class="fa fa-truck"></i>
                                    <a v-on:click.stop :href="bordereauLivraison.status.trackingURL" target="_blank">Tracking</a> 
                                </Badge>
                            </span>
                        </div>
                    </ListItem>

                </li>

            </ul>

            <div class="list__next" v-if="hasMore">
                <Loader v-if="isLoadingMore"></Loader>
                <button
                    class="button"
                    type="button"
                    @click="next"
                    v-else>Voir les bordereaux de livraison suivants</button>
            </div>
            
        </List>

        <div class="container_list container_list--no-result" v-if="isReady && items.length == 0">
            <h1 class="woocommerce-order-details__title">Aucun bordereau de livraison</h1>
        </div>

    </div>
</template>

<script>
import { mapState } from 'vuex'

import Address from '@/components/address/Address.vue'
import Badge from '@/components/badge/Badge.vue'
import Button from '@/components/button/Button.vue'
import ClientFilter from '@/components/list/filters/ClientFilter.vue'
import Date from '@/components/date/Date.vue'
import DateFilter from "@/components/list/filters/DateFilter.vue"
import List, { ListFilter, ListItem, Sorter }  from '@/components/list/List.vue'
import Loader from '@/components/loader/Loader.vue'
import Select  from '@/components/select/Select.vue'
import SiteFilter from '@/components/list/filters/SiteFilter.vue'

import api from '@/api/proxyWeb/bordereauxLivraison'
import { routes } from '@/router'

import filtering from "@/containers/filtering";

export default {

    name: 'BordereauListList',

    data: function() {
        return {
            filters: {
                client_id: null,
                from: null,
                to: null,
                query: '',
                site_id: null,
                status: null
            },
            route: routes.BORDEREAU_LIVRAISON,
            processingXlsx: false
        }
    },

    computed: {

        status: () => {

            return [
                { value: 'billed', name: 'Facturée' },
                { value: 'returned', name: "Retournée" }
            ]
        },

        ...mapState({

            clients: state => state.clients.all,

            clientAddresses: function(state) {

                if ( !state.clients.all ) {
                    return []
                }

                let clientAddresses = []
                state.clients.all.forEach(client => {
                    if ( !this.filters.client_id ) {
                        clientAddresses = [ ...clientAddresses, ...client.clientAddresses ]
                    }
                    else if ( client.id == this.filters.client_id ) {
                        clientAddresses = client.clientAddresses
                    }
                });

                return clientAddresses
            },

            markets: state => state.sites.markets
        })
    },

    methods: {
        
        downloadXlsx() {
            this.processingXlsx = true
            api.getXlsx(this.params)
                .then(() => { this.$notify.success('Téléchargement en cours...') })
                .finally(() => { this.processingXlsx = false } )
        }, 
                
    },    
  
    created () {
        this.useFilter(api)
    },

    components: {
        Address,
        Badge,
        Button,
        ClientFilter,
        Date,
        DateFilter,
        List,
        ListFilter,
        ListItem,
        Loader,
        Select,
        SiteFilter,
        Sorter
    },

    mixins: [ filtering ]
}
</script>

<style scoped>

.tracking a {
    margin-left: 0.5rem;
    color: #fff;
}

.tracking .badge.badge--info {
    background-color: #29235C;
}

</style>
